import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAnnouncements } from "@/context/AnnouncementsContext";
import { AnnouncementBody, PriorityBadge } from "@/components/AnnouncementBody";
import { AnnouncementFullscreen } from "@/components/AnnouncementFullscreen";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const needsAttention = (a) =>
    !a.presented_at || (a.requires_acknowledgment && !a.acknowledged_at);

/**
 * Forced first-view queue. Shows one announcement at a time (urgent first —
 * the server already sorts /announcements/mine by priority). Announcements that
 * require acknowledgment are a hard block: no close button, no overlay click,
 * no keyboard escape until the employee confirms.
 */
export function AnnouncementQueue() {
    const { announcements, patch } = useAnnouncements();
    const [activeId, setActiveId] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);
    const handled = useRef(new Set());
    const presented = useRef(new Set());

    const pendingIds = announcements
        .filter((a) => needsAttention(a) && !handled.current.has(a.id))
        .map((a) => a.id)
        .join(",");
    const knownIds = announcements.map((a) => a.id).join(",");

    // Pin the current item so marking it "presented" doesn't close it.
    useEffect(() => {
        if (activeId) {
            if (!knownIds.split(",").includes(activeId)) setActiveId(null);
            return;
        }
        const first = pendingIds ? pendingIds.split(",")[0] : null;
        if (first) {
            setConfirmed(false);
            setActiveId(first);
        }
    }, [activeId, pendingIds, knownIds]);

    const active = announcements.find((a) => a.id === activeId) || null;
    const remaining = pendingIds ? pendingIds.split(",").length : 0;

    // Presented is its own tracked event, recorded the moment it is shown.
    useEffect(() => {
        if (!active || active.presented_at || presented.current.has(active.id)) return;
        presented.current.add(active.id);
        api.post(`/announcements/${active.id}/presented`)
            .then(({ data }) => patch(active.id, { presented_at: data?.presented_at }))
            .catch(() => presented.current.delete(active.id));
    }, [active, patch]);

    useEffect(() => {
        if (!active) return;
        const block = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        document.addEventListener("keydown", block, true);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", block, true);
            document.body.style.overflow = prev;
        };
    }, [active]);

    if (!active) return null;

    const mustAcknowledge = active.requires_acknowledgment && !active.acknowledged_at;

    const next = () => {
        handled.current.add(active.id);
        setConfirmed(false);
        setActiveId(null);
    };

    const acknowledge = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/announcements/${active.id}/acknowledge`);
            patch(active.id, { acknowledged_at: data?.acknowledged_at });
            toast.success("Acknowledged. Thank you.");
            next();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not acknowledge");
        } finally {
            setBusy(false);
        }
    };

    // Image / video notices get the immersive full-screen experience.
    if (!active.locked && active.media) {
        return (
            <AnnouncementFullscreen
                announcement={active}
                mustAcknowledge={mustAcknowledge}
                confirmed={confirmed}
                onConfirm={setConfirmed}
                onAcknowledge={acknowledge}
                busy={busy}
                onClose={mustAcknowledge ? null : next}
                counterText={remaining > 1 ? `1 of ${remaining} unread notices` : null}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-[100] bg-[#001A33]/80 flex items-start sm:items-center justify-center overflow-y-auto p-4"
            data-testid="announcement-queue-overlay">
            <div className="w-full max-w-xl bg-white rounded-lg shadow-2xl my-auto"
                data-testid="announcement-queue-modal">
                <div className="px-5 sm:px-7 py-4 border-b border-[#E2E8F0] flex items-center justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                            {mustAcknowledge ? "Action required" : "New notice"}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5" data-testid="announcement-queue-counter">
                            {remaining > 1 ? `1 of ${remaining} unread notices` : "1 unread notice"}
                        </div>
                    </div>
                    <PriorityBadge priority={active.priority} />
                </div>

                <div className="px-5 sm:px-7 py-6">
                    <AnnouncementBody
                        announcement={active}
                        onUnlocked={(data) => patch(active.id, data)}
                    />
                </div>

                <div className="px-5 sm:px-7 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC] rounded-b-lg">
                    {mustAcknowledge ? (
                        <>
                            <label className="flex items-start gap-2.5 cursor-pointer select-none">
                                <input
                                    type="checkbox" checked={confirmed} disabled={active.locked}
                                    onChange={(e) => setConfirmed(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 accent-[#003366]"
                                    data-testid="announcement-confirm-checkbox"
                                />
                                <span className="text-sm text-[#1F2A44]">
                                    I have read and understood this notice.
                                </span>
                            </label>
                            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                <Button
                                    onClick={acknowledge}
                                    disabled={busy || !confirmed || active.locked}
                                    className="bg-[#003366] hover:bg-[#0066D9] h-11"
                                    data-testid="announcement-acknowledge-button"
                                >
                                    {busy ? "Sending…" : "Acknowledge"}
                                </Button>
                                <span className="text-xs text-slate-500" data-testid="announcement-hard-block-note">
                                    {active.locked
                                        ? "Unlock this notice with your PIN to acknowledge it."
                                        : "You must acknowledge this notice to continue using the portal."}
                                </span>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <Button onClick={next} className="bg-[#003366] hover:bg-[#0066D9] h-11"
                                data-testid="announcement-got-it-button">
                                Got it
                            </Button>
                            <span className="text-xs text-slate-500">
                                You can read this notice again any time under Notices.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
